import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import {
  Loader2,
  Mail,
  UserPlus,
  Trash2,
  Clock,
  Calendar as CalendarIcon,
  MoreHorizontal,
  UserX,
  UserCheck,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { hasRole, ROLE_LABEL, ROLES_LIST } from "@/lib/roles";

const formatDate = (iso) =>
  new Date(iso).toLocaleString("fr-BE", { dateStyle: "short", timeStyle: "short" });

export default function TeamPage() {
  const { state } = useAuth();
  const currentUser = state?.user;
  const [members, setMembers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [googleByUser, setGoogleByUser] = useState({});
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("commercial");
  const [inviting, setInviting] = useState(false);
  // BLOC 19/10 — Dialog confirmation désactivation membre (P0 LLDLS)
  const [deactivateTarget, setDeactivateTarget] = useState(null);
  const [deactivating, setDeactivating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [m, inv, gc] = await Promise.all([
        api.get("/team/members"),
        api.get("/team/invitations"),
        // Phase B2 — Colonne Agenda dans Paramètres > Équipe
        api.get("/team/members/google-connections").catch(() => ({ data: [] })),
      ]);
      setMembers(m.data);
      setInvitations(inv.data);
      const gMap = {};
      for (const g of gc.data || []) gMap[g.user_id] = g;
      setGoogleByUser(gMap);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const sendInvite = async () => {
    setInviting(true);
    try {
      await api.post("/team/invite", { email: inviteEmail.trim().toLowerCase(), role: inviteRole });
      toast.success("Invitation envoyée", { description: "Email en mode test — visible dans le journal des emails." });
      setInviteOpen(false);
      setInviteEmail("");
      setInviteRole("commercial");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setInviting(false);
    }
  };

  const changeRole = async (m, role) => {
    if (m.role === role) return;
    try {
      await api.patch(`/team/members/${m.id}/role`, { role });
      toast.success(`Rôle mis à jour : ${role}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  // BLOC 19/10 21h40 — Hardening P0 : garde explicite sur deactivateTarget.id
  // pour éviter URL `.../undefined/deactivate` → 404 + message trompeur.
  const confirmDeactivate = async () => {
    if (!deactivateTarget || !deactivateTarget.id) {
      // eslint-disable-next-line no-console
      console.error("[deactivate] target absent or missing id", deactivateTarget);
      toast.error("Membre invalide — rechargez la page.");
      setDeactivateTarget(null);
      return;
    }
    setDeactivating(true);
    try {
      await api.patch(`/team/members/${deactivateTarget.id}/deactivate`);
      toast.success(`${deactivateTarget.first_name} ${deactivateTarget.last_name} désactivé(e)`);
      setDeactivateTarget(null);
      load();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("[deactivate] failed", {
        status: e?.response?.status,
        url: e?.config?.url,
        detail: e?.response?.data,
      });
      // Si le backend renvoie un 400 FR explicite, l'affiche tel quel.
      // Sinon un fallback générique mentionnant le status pour support.
      const status = e?.response?.status;
      const detail = formatApiError(e);
      if (status === 405 || status === 404) {
        toast.error(
          "Action indisponible. Rechargez la page (Ctrl+Shift+R / supprimer le cache Safari) et réessayez.",
        );
      } else {
        toast.error(detail);
      }
    } finally {
      setDeactivating(false);
    }
  };

  const reactivateMember = async (m) => {
    try {
      await api.patch(`/team/members/${m.id}/reactivate`);
      toast.success(`${m.first_name} ${m.last_name} réactivé(e)`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const revokeInvite = async (inv) => {
    try {
      await api.delete(`/team/invitations/${inv.id}`);
      toast.success("Invitation révoquée");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-6" data-testid="team-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">Équipe</h1>
          <p className="text-sm text-slate-400">
            Gérez les membres et invitations de votre entreprise.
          </p>
        </div>
        <Button
          onClick={() => setInviteOpen(true)}
          className="bg-[#C4553B] text-white hover:bg-[#A8402C]"
          data-testid="invite-btn"
        >
          <UserPlus className="mr-2 h-4 w-4" /> Inviter un membre
        </Button>
      </div>

      {loading ? (
        <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
      ) : (
        <>
          <Card className="border-slate-800 bg-slate-900/40">
            <CardContent className="p-0">
              {/* BLOC 19/10 — Wrapper overflow-x-auto pour éviter débord iPad/iPhone.
                  Les actions passent en menu compact `…` (DropdownMenu shadcn)
                  pour garantir leur accessibilité sans scroll horizontal. */}
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="team-members-table">
                  <thead className="border-b border-slate-800 text-slate-400">
                    <tr>
                      <th className="p-3 text-left">Nom</th>
                      <th className="p-3 text-left">Email</th>
                      <th className="p-3 text-left">Rôle</th>
                      <th className="p-3 text-left">Agenda</th>
                      <th className="p-3 text-left">Dernière connexion</th>
                      <th className="sticky right-0 bg-slate-900 p-3 text-right w-12 shadow-[-8px_0_12px_-8px_rgba(0,0,0,0.6)]">
                        Actions
                      </th>
                    </tr>
                  </thead>
                <tbody>
                  {members.map((m) => {
                    const g = googleByUser[m.id];
                    // BLOC 19/10 — Count admins ACTIFS uniquement pour garde-fou
                    // dernier admin (côté front, miroir du check backend).
                    const activeAdminsCount = members.filter(
                      (x) => hasRole(x, ["admin"]) && x.is_active !== false,
                    ).length;
                    const isSelf = currentUser?.id === m.id;
                    const isActive = m.is_active !== false;
                    const isLastActiveAdmin =
                      hasRole(m, ["admin"]) && isActive && activeAdminsCount <= 1;
                    const roleChangeBlocked =
                      hasRole(m, ["admin"]) && (isSelf || isLastActiveAdmin);
                    const roleChangeReason = isLastActiveAdmin
                      ? "Cet utilisateur est le dernier Admin actif du tenant. Promouvez un autre utilisateur en Admin avant de retirer ce rôle."
                      : isSelf
                        ? "Vous ne pouvez pas retirer votre propre rôle Admin. Demandez à un autre Admin de le faire."
                        : "";
                    const deactivateBlocked = isSelf || isLastActiveAdmin;
                    const deactivateReason = isSelf
                      ? "Vous ne pouvez pas désactiver votre propre compte."
                      : isLastActiveAdmin
                        ? "Cet utilisateur est le dernier Admin actif du tenant. Promouvez un autre utilisateur en Admin avant de le désactiver."
                        : "";
                    return (
                    <tr
                      key={m.id}
                      className={`border-b border-slate-800/60 last:border-0 ${isActive ? "" : "opacity-60"}`}
                      data-testid={`team-row-${m.email}`}
                    >
                      <td className="p-3 font-medium">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span>{m.first_name} {m.last_name}</span>
                          {!isActive && (
                            <Badge
                              className="bg-slate-700 text-slate-300 border-slate-600 text-[10px]"
                              data-testid={`deactivated-badge-${m.email}`}
                            >
                              Désactivé
                            </Badge>
                          )}
                        </div>
                      </td>
                      <td className="p-3 text-slate-400">{m.email}</td>
                      <td className="p-3">
                        <Select
                          value={m.role}
                          onValueChange={(v) => changeRole(m, v)}
                          disabled={roleChangeBlocked || !isActive}
                        >
                          <SelectTrigger
                            className="h-8 w-32 border-slate-700 bg-slate-950/40 text-xs disabled:opacity-50"
                            data-testid={`role-select-${m.email}`}
                            title={roleChangeBlocked ? roleChangeReason : (!isActive ? "Réactivez le membre avant de modifier son rôle." : "")}
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="border-slate-700 bg-slate-900">
                            {ROLES_LIST.map((r) => (
                              <SelectItem key={r} value={r} data-testid={`role-option-${r}`}>
                                {ROLE_LABEL[r]}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="p-3 text-xs" data-testid={`agenda-cell-${m.email}`}>
                        {g && g.google_email ? (
                          <span className="inline-flex items-center gap-1.5 text-slate-300">
                            <CalendarIcon className="h-3.5 w-3.5 text-[#C4553B]" />
                            <span className="truncate max-w-[200px]" title={g.google_email}>
                              {g.google_email}
                            </span>
                            {!g.sync_enabled && (
                              <Badge className="badge-neutral text-[10px]">Sync off</Badge>
                            )}
                          </span>
                        ) : (
                          <span className="text-slate-500" title="Le membre n'a pas encore connecté son agenda Google">
                            Non connecté
                          </span>
                        )}
                      </td>
                      <td className="p-3 text-xs text-slate-500">
                        {m.last_login_at ? formatDate(m.last_login_at) : "—"}
                      </td>
                      <td className="sticky right-0 bg-slate-900 p-3 text-right w-12 shadow-[-8px_0_12px_-8px_rgba(0,0,0,0.6)]">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button
                              className="rounded p-1.5 text-slate-400 hover:bg-slate-700/50 hover:text-slate-200"
                              aria-label={`Actions pour ${m.first_name} ${m.last_name}`}
                              data-testid={`member-actions-${m.email}`}
                            >
                              <MoreHorizontal className="h-4 w-4" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent
                            align="end"
                            className="border-slate-700 bg-slate-900 text-slate-100"
                          >
                            {isActive ? (
                              <DropdownMenuItem
                                onClick={() => !deactivateBlocked && setDeactivateTarget(m)}
                                disabled={deactivateBlocked}
                                className="text-red-400 focus:bg-red-500/10 focus:text-red-300 disabled:opacity-40"
                                title={deactivateBlocked ? deactivateReason : ""}
                                data-testid={`deactivate-member-${m.email}`}
                              >
                                <UserX className="mr-2 h-4 w-4" /> Désactiver l'accès
                              </DropdownMenuItem>
                            ) : (
                              <DropdownMenuItem
                                onClick={() => reactivateMember(m)}
                                className="text-emerald-400 focus:bg-emerald-500/10 focus:text-emerald-300"
                                data-testid={`reactivate-member-${m.email}`}
                              >
                                <UserCheck className="mr-2 h-4 w-4" /> Réactiver l'accès
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  );})}
                </tbody>
              </table>
              </div>
            </CardContent>
          </Card>

          <div>
            <h2 className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-300">
              <Clock className="h-4 w-4" /> Invitations en cours ({invitations.length})
            </h2>
            {invitations.length === 0 ? (
              <p className="text-sm text-slate-500">Aucune invitation en attente.</p>
            ) : (
              <Card className="border-slate-800 bg-slate-900/40">
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="border-b border-slate-800 text-slate-400">
                        <tr>
                          <th className="p-3 text-left">Email</th>
                          <th className="p-3 text-left">Rôle</th>
                          <th className="p-3 text-left">Envoyée</th>
                          <th className="p-3 text-left">Expire</th>
                          <th className="p-3 text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {invitations.map((inv) => (
                          <tr key={inv.id} className="border-b border-slate-800/60 last:border-0">
                            <td className="p-3 flex items-center gap-2">
                              <Mail className="h-3.5 w-3.5 text-slate-500" />
                              {inv.email}
                            </td>
                            <td className="p-3">
                              <Badge
                                variant="outline"
                                className="border-slate-600 bg-slate-800/60 text-xs text-slate-300"
                              >
                                {ROLE_LABEL[inv.role] || inv.role}
                              </Badge>
                            </td>
                            <td className="p-3 text-xs text-slate-500">
                              {formatDate(inv.created_at)}
                            </td>
                            <td className="p-3 text-xs text-slate-500">
                              {formatDate(inv.expires_at)}
                            </td>
                            <td className="p-3 text-right">
                              <button
                                onClick={() => revokeInvite(inv)}
                                className="rounded p-1.5 text-slate-400 hover:bg-red-500/10 hover:text-red-400"
                                data-testid={`revoke-invite-${inv.email}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </>
      )}

      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent className="border-slate-800 bg-slate-900 text-slate-100">
          <DialogHeader>
            <DialogTitle>Inviter un membre</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-slate-300">Email</Label>
              <Input
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                className="border-slate-700 bg-slate-950/40"
                data-testid="invite-email-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-slate-300">Rôle</Label>
              <Select value={inviteRole} onValueChange={setInviteRole}>
                <SelectTrigger className="border-slate-700 bg-slate-950/40" data-testid="invite-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="border-slate-700 bg-slate-900">
                  {ROLES_LIST.map((r) => (
                    <SelectItem key={r} value={r} data-testid={`invite-role-option-${r}`}>
                      {ROLE_LABEL[r]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setInviteOpen(false)}
              className="border-slate-700 bg-slate-800 text-slate-200"
            >
              Annuler
            </Button>
            <Button
              onClick={sendInvite}
              disabled={!inviteEmail.trim() || inviting}
              className="bg-[#C4553B] text-white hover:bg-[#A8402C]"
              data-testid="invite-send-btn"
            >
              {inviting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Envoyer l'invitation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* BLOC 19/10 — Dialog confirmation désactivation. Texte EXACT demandé Christophe. */}
      <Dialog
        open={deactivateTarget !== null}
        onOpenChange={(open) => !open && setDeactivateTarget(null)}
      >
        <DialogContent
          className="border-slate-800 bg-slate-900 text-slate-100"
          data-testid="deactivate-dialog"
        >
          <DialogHeader>
            <DialogTitle>Désactiver l'accès</DialogTitle>
            <DialogDescription className="text-slate-400">
              {deactivateTarget && (
                <>
                  {deactivateTarget.first_name} {deactivateTarget.last_name} ne pourra plus se
                  connecter. Son nom reste sur ses devis et factures.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeactivateTarget(null)}
              disabled={deactivating}
              className="border-slate-700 bg-slate-800 text-slate-200"
              data-testid="deactivate-cancel-btn"
            >
              Annuler
            </Button>
            <Button
              onClick={confirmDeactivate}
              disabled={deactivating}
              className="bg-red-600 text-white hover:bg-red-700"
              data-testid="deactivate-confirm-btn"
            >
              {deactivating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Désactiver
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
